import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem } from '@material-ui/core';
import axios from 'axios';

//https://api.precisiononcall.com:8088/api/?q=${code}
//http://localhost:8080/api/?q=${code}

const localizer = momentLocalizer(moment);

const jobTypes = [
  'Radtech', 'Circulating RN', 'PACU Nurse', 'PreOp/PACU RN', 'Surg tech', 'PCT or Other', 'SPD tech', 'Other RN', 'PreOp Nurse', 'First Assist'
];

const jobTypeColors = {
  'Radtech': '#ff7e79',
  'Circulating RN': '#4ecdc4',
  'PACU Nurse': '#00aad6',
  'PreOp/PACU RN': '#ffa07a',
  'Surg tech': '#3da8aa',
  'PCT or Other': '#d7a800',
  'SPD tech': '#bb8fce',
  'Other RN': '#00d4d6',
  'PreOp Nurse': '#78adff',
  'First Assist': '#78a779'
}

// min/max control the time-of-day range shown in Week/Day views.
// Use start-of-day to end-of-day so the full 24 hours is always visible
// and no early-morning or late-night shifts get clipped off the grid.
const minDate = moment().startOf('day').toDate();
const maxDate = moment().endOf('day').toDate();

const WebScheduler = ({ initialSiteCode, initialSiteQuery, preloadedJobs }) => {

  // Helper functions for localStorage
  const getStoredDate = () => {
    try {
      const stored = localStorage.getItem('calendar-date');
      return stored ? new Date(stored) : new Date();
    } catch (error) {
      console.error('Error reading date from localStorage:', error);
      return new Date();
    }
  };

  const getStoredView = () => {
    try {
      const stored = localStorage.getItem('calendar-view');
      return stored || 'month';
    } catch (error) {
      console.error('Error reading view from localStorage:', error);
      return 'month';
    }
  };
  
  const [currentDate, setCurrentDate] = useState(getStoredDate);
  const [currentView, setCurrentView] = useState(getStoredView);  
  const [errorText, setErrorText] = useState('');
  const [showError, setShowError] = useState(false);
  const [topErrorText, setTopErrorText] = useState('');
  const [showTopError, setShowTopError] = useState(false);
  const [textClass, setTextClass] = useState('errorText');
  const [events, setEvents] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [siteCode, setSiteCode] = useState(null);
  const [siteQuery, setSiteQuery] = useState(null);  // ADDED: Track site query in state
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(true);
  const [newJob, setNewJob] = useState({
    title: '',
    jobType: '',
    description: '',
    start: moment().add(1, 'day').toDate(),
    end: moment().add(1, 'day').add(1, 'hour').toDate(),
  });

  const promptSiteCode = useCallback(() => {
    const code = prompt("Please enter your site code:");
    if (code) {
      setSiteCode(code);
      const newUrl = `${window.location.pathname}?q=${code}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
    } else {
      promptSiteCode();
    }
  }, []);

  useEffect(() => {
    const storedDate = getStoredDate();
    const storedView = getStoredView();
    
    setCurrentDate(storedDate);
    setCurrentView(storedView);
  }, []);  

  useEffect(() => {
    const checkSiteCode = () => {
      if (initialSiteCode) {
        setSiteCode(initialSiteCode);
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        const codeFromUrl = urlParams.get('q');
        if (codeFromUrl) {
          setSiteCode(codeFromUrl);
        } else {
          promptSiteCode();
        }
      }
    };

    checkSiteCode();
  }, [initialSiteCode, promptSiteCode]);

  // ADDED: New useEffect to update siteQuery when initialSiteQuery changes
  useEffect(() => {
    if (initialSiteQuery) {
      setSiteQuery(initialSiteQuery);
      console.log('Site query updated:', initialSiteQuery);
    }
  }, [initialSiteQuery]);

  useEffect(() => {
    setLoading(true);
    // If we have preloaded jobs, use them instead of fetching
    if (preloadedJobs) {
      const fetchedEvents = preloadedJobs.map(job => ({
        title: job.title,
        jobType: job.jobType,
        start: new Date(job.startTime * 1000),
        end: new Date(job.endTime * 1000),
        shiftId: job.shiftId,
        description: job.description,
        staff: job.staff
      }));
      setEvents(fetchedEvents);
      setLoading(false);
    } else if (siteCode) {
      //fetchEvents(siteCode);
    }
  }, [siteCode, preloadedJobs]);

  const fetchEvents = useCallback(async (code) => {
    try {
      setLoading(true);
      var urlTarget;
      if (siteQuery) {
        urlTarget = `https://api.precisiononcall.com:8088/api/?q=${code}&s=${encodeURIComponent(siteQuery.replace(/['"]+/g, ''))}`;
      } else {
        urlTarget = `https://api.precisiononcall.com:8088/api/?q=${code}`;
      }      
      const response = await axios.get(urlTarget);
      if (response.status === 200) {
        if (response.data.jobs) {
          const fetchedEvents = response.data.jobs.map(job => ({
            title: job.title,
            jobType: job.jobType,
            start: new Date(job.startTime * 1000),
            end: new Date(job.endTime * 1000),
            shiftId: job.shiftId,
            description: job.description,
            staff: job.staff
          }));
          setEvents(fetchedEvents);
        }
        setIsAdmin(response.data.admin === true);
      } else {
        console.error('Failed to fetch events or no events returned');
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [siteQuery]);

  const handleNavigate = (date) => {
    setCurrentDate(date);
    try {
      localStorage.setItem('calendar-date', date.toISOString());
    } catch (error) {
      console.error('Error saving date to localStorage:', error);
    }
  };
  const handleViewChange = (view) => {
    setCurrentView(view);
    try {
      localStorage.setItem('calendar-view', view);
    } catch (error) {
      console.error('Error saving view to localStorage:', error);
    }
  };    

  const handleSelectSlot = (slotInfo) => {
    if (!isAdmin) return;
    const now = moment();
    const slotStart = moment(slotInfo.start);
    if (slotStart.isBefore(now.add(0, 'hours'))) {
      //alert("The WebScheduler cannot create a shift less than 12 hours from now.\nPlease call or text (727) 434-0525 for assistance");
      setTopErrorText('The WebScheduler cannot create a shift less than 12 hours from now.\nPlease call or text (727) 434-0525 for assistance');
      setShowTopError(true);
      handleErrorText();
      return;
    }
    setNewJob({
      ...newJob,
      start: moment(slotInfo.start).add(6, 'hour').toDate(),
      end: moment(slotInfo.start).add(16, 'hour').toDate(),
    });
    setShowError(false);
    setErrorText('');
    setShowTopError(false);
    setTopErrorText('');    
    setOpenModal(true);
  };

  const handleErrorText = () => {
    /*
    const newText = document.getElementById('newErrorText');
    const editText = document.getElementById('editErrorText');

    newText.classList.add('errorTextHighlight');
    editText.classList.add('errorTextHighlight');
    */
    setTextClass('errorTextHighlight');

    setTimeout(() => {
      setTextClass('errorText');
    }, "100");

    setTimeout(() => {
      setTextClass('errorTextHighlight');
    }, "600");      
  }

  const handleCloseModal = () => {
    setOpenModal(false);
    setOpenDetailsModal(false);
    setOpenEditModal(false);
    setShowError(false);
    setErrorText('');
    setShowTopError(false);
    setTopErrorText('');    
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewJob({ ...newJob, [name]: value });
  };

  const validateJobTimes = () => {
    const start = moment(newJob.start);
    const end = moment(newJob.end);
    if (end.isBefore(start)) {
      //alert("End time cannot be before start time.");
      setShowError(true);
      setErrorText('End time cannot be before start time.');
      handleErrorText();
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateJobTimes()) return;
    if (moment(newJob.start).isBefore(moment().add(12, 'hours'))) {
      //alert("You cannot set a job to start less than 12 hours in the future.\nPlease call or text (727) 434-0525 for assistance");
      setShowError(true);
      setErrorText('You cannot set a job to start less than 12 hours in the future.\nPlease call or text (727) 434-0525 for assistance');
      handleErrorText();
      return;
    }    
    if (!jobTypes.includes(newJob.jobType)) {
      //return alert("You must select a job type");
      setShowError(true);
      setErrorText('You must select a job type');
      handleErrorText();      
      return;
    }
    if (newJob.title.length < 1) {
      setShowError(true);
      setErrorText('The job Title cannot be blank.');
      handleErrorText(); 
      return;
    }    
    if (newJob.description.length < 1) {
      setShowError(true);
      setErrorText('The job Case Details cannot be blank.');
      handleErrorText(); 
      return;
    }    
    var jobData = {};
    for (const key in newJob) {
      jobData[key] = newJob[key];
    }
    jobData.start = moment(jobData.start).unix();
    jobData.end = moment(jobData.end).unix();
    let newURL;
    if (siteQuery) {
      newURL = `https://api.precisiononcall.com:8088/api/?q=${siteCode}&s=${encodeURIComponent(siteQuery.replace(/['"]+/g, ''))}`
      console.log('Submitting job with site query:', siteQuery);
    } else {
      newURL = `https://api.precisiononcall.com:8088/api/?q=${siteCode}`;
      console.log('Submitting job without site query');
    }
    console.log('Request URL:', newURL);
    try {
      const response = await axios.post(newURL, jobData, {headers: {'content-type': 'application/json'}});
      if ((response.status === 200) || (response.status === 201) || (response.status === 202)) {
        const pendingJob = {
          ...newJob, title: newJob.title
        };
        setEvents([...events, pendingJob]);
        setOpenModal(false);
        setNewJob({
          title: '',
          jobType: '',
          description: '',
          start: moment().add(1, 'day').toDate(),
          end: moment().add(1, 'day').add(1, 'hour').toDate(),
        });
        setTimeout(() => {
          window.location.reload(true);
        }, "1500"); 
      } else {
        console.error('Failed to create job');
        setShowError(true);
        setErrorText('Failed to create job. Status code: ' + response.status);
        handleErrorText();
      }
    } catch (error) {
      console.error('Error creating job:', error);
      setShowError(true);
      setErrorText('Error creating job: ' + error);
      handleErrorText();      
    }
  };

  const handleEventSelect = (event) => {
    // The "+X more shifts" pill isn't a real shift — clicking it drops into
    // that day's Day view so every shift is visible with full detail.
    if (event.__isMore) {
      handleNavigate(event.__date);
      handleViewChange('day');
      return;
    }
    setSelectedEvent(event);
    setShowError(false);
    setErrorText('');
    setShowTopError(false);
    setTopErrorText('');
    setOpenDetailsModal(true);
  };

  const handleSaveEdit = async () => {
    if (!isAdmin) return;
    if (!validateJobTimes()) return;
    if (moment(selectedEvent.start).isBefore(moment().add(12, 'hours'))) {
      //alert("You cannot edit a job that is less than 12 hours in the future.\nPlease call or text (727) 434-0525 for assistance");
      setShowError(true);
      setErrorText('You cannot edit a job that is less than 12 hours in the future.\nPlease call or text (727) 434-0525 for assistance');
      handleErrorText();      
      return;
    }
    if (moment(newJob.start).isBefore(moment().add(12, 'hours'))) {
      //alert("You cannot set a job to start less than 12 hours in the future.\nPlease call or text (727) 434-0525 for assistance");
      setShowError(true);
      setErrorText('You cannot set a job to start less than 12 hours in the future.\nPlease call or text (727) 434-0525 for assistance');
      handleErrorText();      
      return;
    }
    if (!jobTypes.includes(newJob.jobType)) {
      setShowError(true);
      setErrorText('You must select a job type');
      handleErrorText();      
      return;
    }    
    if (newJob.title.length < 1) {
      setShowError(true);
      setErrorText('The job Title cannot be blank.');
      handleErrorText(); 
      return;
    }    
    if (newJob.description.length < 1) {
      setShowError(true);
      setErrorText('The job Case Details cannot be blank.');
      handleErrorText(); 
      return;
    }    
    var jobData = {};
    for (const key in newJob) {
      jobData[key] = newJob[key];
    }
    jobData.start = moment(jobData.start).unix();
    jobData.end = moment(jobData.end).unix();
    jobData.shiftId = selectedEvent.shiftId;
    let newURL;
    if (siteQuery) {
      newURL = `https://api.precisiononcall.com:8088/api/?q=${siteCode}&s=${encodeURIComponent(siteQuery.replace(/['"]+/g, ''))}`
      console.log('Submitting job with site query:', siteQuery);
    } else {
      newURL = `https://api.precisiononcall.com:8088/api/?q=${siteCode}`;
      console.log('Submitting job without site query');
    }    
    try {
      const response = await axios.put(newURL, jobData, {headers: {'content-type': 'application/json'}});
      if ((response.status === 200) || (response.status === 201) || (response.status === 202)) {
        setOpenEditModal(false);
        setNewJob({
          title: '',
          jobType: '',
          description: '',
          start: moment().add(1, 'day').toDate(),
          end: moment().add(1, 'day').add(1, 'hour').toDate(),
        });
        setTimeout(() => {
          window.location.reload(true);
        }, "1500");
      } else {
        console.error('Failed to update job');
        setShowError(true);
        setErrorText('Failed to update job. Status code: ' + response.status);
        handleErrorText();        
      }
    } catch (error) {
      console.error('Error updating job:', error);
      setShowError(true);
      setErrorText('Error updating job: ' + error);
      handleErrorText();       
    }
  };

  const handleCopyJob = () => {
    setNewJob({
      title: selectedEvent.title,
      jobType: selectedEvent.jobType,
      description: selectedEvent.description,
      start: moment().add(1, 'day').toDate(),
      end: moment().add(1, 'day').add(1, 'hour').toDate(),
    });
    setOpenDetailsModal(false);
    setShowError(false);
    setErrorText('');    
    setOpenModal(true);
  };

  const handleEditJob = () => {
    setNewJob({
      title: selectedEvent.title,
      jobType: selectedEvent.jobType,
      description: selectedEvent.description,
      start: selectedEvent.start,
      end: selectedEvent.end,
    });
    setOpenDetailsModal(false);
    setShowError(false);
    setErrorText('');    
    setOpenEditModal(true);
  };

  const handleCancelJob = async () => {
    if (!window.confirm("Are you sure you want to cancel this job?")) return;

    var jobData = {};
    jobData.shiftId = selectedEvent.shiftId;
    jobData.jobType = selectedEvent.jobType;
    jobData.cancelled = true;
    let newURL;
    if (siteQuery) {
      newURL = `https://api.precisiononcall.com:8088/api/?q=${siteCode}&s=${encodeURIComponent(siteQuery.replace(/['"]+/g, ''))}`
    } else {
      newURL = `https://api.precisiononcall.com:8088/api/?q=${siteCode}`;
    }    
    try {
      const response = await axios.delete(newURL, {
        data: jobData,
        headers: {'content-type': 'application/json'}
      });
      if ((response.status === 200) || (response.status === 201) || (response.status === 202)) {
        setOpenDetailsModal(false);
        setTimeout(() => {
          window.location.reload(true);
        }, "1500");
      } else {
        console.error('Failed to cancel job');
        setShowError(true);
        setErrorText('Failed to cancel job. Status code: ' + response.status);
        handleErrorText();         
      }
    } catch (error) {
      console.error('Error canceling job:', error);
      setShowError(true);
      setErrorText('Error canceling job: ' + error);
      handleErrorText();       
    }
  };

  // In month view, cap how many shifts render per day and replace the rest
  // with a single clickable "+X more shifts" pill. This keeps the visible
  // shifts full-size (two lines) while guaranteeing the overflow indicator
  // always shows, instead of relying on React Big Calendar's month-view
  // auto-measurement (which clips events with a fixed-height container).
  const MONTH_MAX_VISIBLE = 2;

  const displayEvents = useMemo(() => {
    if (currentView !== 'month') return events;

    const byDay = {};
    events.forEach((ev) => {
      const key = moment(ev.start).format('YYYY-MM-DD');
      (byDay[key] = byDay[key] || []).push(ev);
    });

    const result = [];
    Object.keys(byDay).forEach((key) => {
      const dayEvents = byDay[key]
        .slice()
        .sort((a, b) => new Date(a.start) - new Date(b.start));

      if (dayEvents.length <= MONTH_MAX_VISIBLE) {
        result.push(...dayEvents);
        return;
      }

      const hiddenCount = dayEvents.length - MONTH_MAX_VISIBLE;
      result.push(...dayEvents.slice(0, MONTH_MAX_VISIBLE));
      result.push({
        __isMore: true,
        __date: moment(key, 'YYYY-MM-DD').toDate(),
        title: `+${hiddenCount} more shift${hiddenCount > 1 ? 's' : ''}`,
        // Late start so the pill always sorts after the day's real shifts.
        start: moment(key, 'YYYY-MM-DD').hour(23).minute(59).toDate(),
        end: moment(key, 'YYYY-MM-DD').hour(23).minute(59).second(59).toDate(),
        jobType: '__more',
      });
    });
    return result;
  }, [events, currentView]);

  const eventStyleGetter = (event) => {
    if (event.__isMore) {
      return {
        style: {
          backgroundColor: '#eef2f7',
          color: '#1E77BA',
          borderRadius: '5px',
          border: '1px solid #cdd9e5',
          fontWeight: 'bold',
          cursor: 'pointer',
          opacity: 1,
        },
      };
    }
    const backgroundColor = jobTypeColors[event.jobType] || '#3174ad';
    // Shifts pending cancellation get a red outline so they stand out.
    const isPendingCancellation =
      typeof event.title === 'string' &&
      event.title.trim().toLowerCase().startsWith('pending cancellation');
    const style = {
      backgroundColor,
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: isPendingCancellation ? '2px solid #d32f2f' : '0px',
      display: 'block'
    };
    return {
      style
    };
  };

  // Edit / Cancel Job are locked within 12h of the shift start.
  // When locked they stay greyed out; otherwise they get their colors.
  const detailsActionsDisabled = moment(selectedEvent?.start).isBefore(moment().add(12, 'hours'));

  return (
    <div>
      {showTopError && (
        <p className={textClass}>
          {topErrorText}
        </p>
      )}      
      <Calendar
        localizer={localizer}
        events={displayEvents}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100vh' }}
        date={currentDate}
        view={currentView}        
        selectable
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleEventSelect}
        onNavigate={handleNavigate}
        onView={handleViewChange}        
        views={['week', 'month', 'day', 'agenda']}
        min={minDate}
        max={maxDate}
        eventPropGetter={eventStyleGetter}
        dayLayoutAlgorithm="no-overlap"
        popup
        messages={{
          showMore: (count) => `+${count} more shift${count > 1 ? 's' : ''}`,
        }}
        formats={{
          eventTimeRangeFormat: ({ start, end }) =>
            `${moment(start).format('MMM DD, YYYY HH:mm')} - ${moment(end).format('MMM DD, YYYY HH:mm')}`,
        }}
        components={{
          event: ({ title, event }) =>
            event.__isMore ? (
              <div className="shift-event-more">{title}</div>
            ) : (
              <div className="shift-event">
                <div className="shift-event-title">{title}</div>
                <div className="shift-event-sub" style={{ fontSize: '0.8em' }}>
                  {event.jobType + " " + event.staff}
                </div>
              </div>
            ),
        }}
      />
      {isAdmin && (
        <Dialog open={openModal} onClose={handleCloseModal}>
          <div className="Modal-header">
            <DialogTitle>Request New Job</DialogTitle>
          </div>
          <DialogContent>
            <TextField
              name="title"
              label="Title"
              fullWidth
              value={newJob.title}
              onChange={handleInputChange}
              margin="normal"
            />
            <Select
              name="jobType"
              value={newJob.jobType}
              onChange={handleInputChange}
              fullWidth
              displayEmpty
            >
              <MenuItem value="" disabled>Select Job Type</MenuItem>
              {jobTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
            <TextField
              name="description"
              label="Case Details"
              fullWidth
              multiline
              value={newJob.description}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              name="start"
              label="Arrival Date & Time"
              type="datetime-local"
              fullWidth
              value={moment(newJob.start).format('YYYY-MM-DDTHH:mm')}
              onChange={handleInputChange}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: moment().add(1, 'day').format('YYYY-MM-DDTHH:mm'),
                max: moment().add(3, 'months').format('YYYY-MM-DDTHH:mm'),
              }}
            />
            <TextField
              name="end"
              label="Estimated End Date & Time"
              type="datetime-local"
              fullWidth
              value={moment(newJob.end).format('YYYY-MM-DDTHH:mm')}
              onChange={handleInputChange}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: moment().add(1, 'day').format('YYYY-MM-DDTHH:mm'),
                max: moment().add(3, 'months').format('YYYY-MM-DDTHH:mm'),
              }}
            />
            <p>Please allow 30 minutes between arrival time and the first case's start time</p>
            {showError && (
              <p className={textClass}>
                {errorText}
              </p>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} variant="contained" style={{ backgroundColor: '#d32f2f', color: 'white' }}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" style={{ backgroundColor: '#1976d2', color: 'white' }}>
              Send Request
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Dialog open={openDetailsModal} onClose={handleCloseModal}>
        <div className="Modal-header">
          <DialogTitle>Job Details</DialogTitle>
        </div>
        <DialogContent>
          {selectedEvent && (
            <>
              <p><strong>Title:</strong> {selectedEvent.title}</p>
              <p><strong>Job Type:</strong> {selectedEvent.jobType}</p>
              <p><strong>Case Details:</strong> {selectedEvent.description}</p>
              <p><strong>Arrival:</strong> {moment(selectedEvent.start).format('MMM DD, YYYY HH:mm')}</p>
              <p><strong>Estimated End:</strong> {moment(selectedEvent.end).format('MMM DD, YYYY HH:mm')}</p>
              <p><strong>Staff Assigned:</strong> {selectedEvent.staff}</p>
            </>
          )}
          {showError && (
            <p className={textClass}>
              {errorText}
            </p>
          )}          
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained" style={{ backgroundColor: '#607d8b', color: 'white' }}>
            Close
          </Button>
          {isAdmin && (
            <>
              <Button onClick={handleCopyJob} variant="contained" style={{ backgroundColor: '#05A1AB', color: 'white' }}>
                Copy
              </Button>            
              <Button onClick={handleEditJob} variant="contained" disabled={detailsActionsDisabled} style={detailsActionsDisabled ? undefined : { backgroundColor: '#1976d2', color: 'white' }}>
                Edit
              </Button>
              <Button onClick={handleCancelJob} variant="contained" disabled={detailsActionsDisabled} style={detailsActionsDisabled ? undefined : { backgroundColor: '#d32f2f', color: 'white' }}>
                Cancel Job
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={openEditModal} onClose={handleCloseModal}>
        <div className="Modal-header">
          <DialogTitle>Edit Job</DialogTitle>
        </div>
        <DialogContent>
          <TextField
            name="title"
            label="Title"
            fullWidth
            value={newJob.title}
            onChange={handleInputChange}
            margin="normal"
          />
          <p>Job Type: {newJob.jobType}</p>
          <TextField
            name="description"
            label="Case Details"
            fullWidth
            multiline
            value={newJob.description}
            onChange={handleInputChange}
            margin="normal"
          />
          <TextField
            name="start"
            label="Arrival Date & Time"
            type="datetime-local"
            fullWidth
            value={moment(newJob.start).format('YYYY-MM-DDTHH:mm')}
            onChange={handleInputChange}
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: moment().add(1, 'day').format('YYYY-MM-DDTHH:mm'),
              max: moment().add(3, 'months').format('YYYY-MM-DDTHH:mm'),
            }}
          />
          <TextField
            name="end"
            label="Estimated End Date & Time"
            type="datetime-local"
            fullWidth
            value={moment(newJob.end).format('YYYY-MM-DDTHH:mm')}
            onChange={handleInputChange}
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: moment().add(1, 'day').format('YYYY-MM-DDTHH:mm'),
              max: moment().add(3, 'months').format('YYYY-MM-DDTHH:mm'),
            }}
          />
          <p>Please allow 30 minutes between arrival time and the first case's start time</p>
          {showError && (
            <p className={textClass}>
              {errorText}
            </p>
          )}          
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained" style={{ backgroundColor: '#d32f2f', color: 'white' }}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" style={{ backgroundColor: '#1976d2', color: 'white' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>      
    </div>
  );
};

export default WebScheduler;